import {
  MediaSegment as ApiMediaSegment,
  MediaSegmentData,
} from '@sparemin/api-sdk';
import { List } from 'immutable';
import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { mediaSegmentDataSchema } from 'redux/middleware/api/media-upload-service/schemas';
import { State } from 'redux/types';
import { createMap, DeepImmutableMap, IImmutableMap } from 'types';
import { entitiesSelector } from './base';

const EMPTY_LIST = List<IImmutableMap<MediaSegment>>();
const EMPTY_OBJECT = {};

export type MediaSegmentDataById = DeepImmutableMap<
  Record<string, MediaSegmentData>
>;

export type MediaSegment = {
  /**
   * the end time of the segment in the timeline accounting for deleted segments
   */
  adjustedEndMillis: number;
  /**
   * the start time of the segment in the timeline accounting for deleted segments
   */
  adjustedStartMillis: number;
  /**
   * the total deleted time from the start of the timeline to the start of the segment
   */
  adjustmentMillis: number;
  /**
   * the duration of the segment in the underlying media's timeline
   */
  durationMillis: number;
  /**
   * the end time of the segment in the underlying media's timeline
   */
  endMillis: number;
  /**
   * the start time of the segment in the underlying media's timeline
   */
  startMillis: number;
};

export type MediaSegmentsById = {
  [id: string]: MediaSegment[];
};

// raw normalized data
const normalizedMediaSegmentDataSelector = (state: State) =>
  entitiesSelector(state).get('mediaSegmentData');

// denormalized data
export const mediaSegmentDataByIdSelector = createSelector(
  entitiesSelector,
  normalizedMediaSegmentDataSelector,
  (entities, mediaSegmentData): MediaSegmentDataById =>
    denormalize(mediaSegmentData, mediaSegmentDataSchema, entities),
);

// TODO SPAR-26252: should be Immutable?
export const mediaSegmentDataSelector = createSelector(
  mediaSegmentDataByIdSelector,
  (_, id: string) => id,
  (mediaSegmentDataById, id) => {
    if (!mediaSegmentDataById || !id) {
      return undefined;
    }

    return mediaSegmentDataById.get(id)?.toJS() as MediaSegmentData;
  },
);

export const mediaSegmentRevisionIdSelector = createSelector(
  mediaSegmentDataSelector,
  mediaSegmentData => mediaSegmentData?.revisionId,
);

// takes the raw media segments and enriches them with data to help with conversions
// and calculations downstream.  a lot of this can be derived but is used so often
// in so many different places that it's worth sovling once for everone to use
function createMediaSegments(
  segments: List<IImmutableMap<ApiMediaSegment>>,
): List<IImmutableMap<MediaSegment>> {
  if (!segments || segments.size === 0) {
    return EMPTY_LIST;
  }

  let totalAdjustmentMillis = 0;

  return segments
    .map((segment, i) => {
      const durationMillis = segment.get('durationMillis');
      const startMillis = segment.get('playOffsetMillis');
      const endMillis = segment.get('playOffsetMillis') + durationMillis;

      if (i === 0) {
        totalAdjustmentMillis += startMillis;
      } else {
        const prevSegment = segments.get(i - 1);
        const prevSegmentEndMillis =
          prevSegment.get('playOffsetMillis') +
          prevSegment.get('durationMillis');
        const adjustmentMillis = startMillis - prevSegmentEndMillis;
        totalAdjustmentMillis += adjustmentMillis;
      }

      return createMap({
        adjustedEndMillis: endMillis - totalAdjustmentMillis,
        adjustedStartMillis: startMillis - totalAdjustmentMillis,
        adjustmentMillis: totalAdjustmentMillis,
        durationMillis,
        startMillis,
        endMillis,
      });
    })
    .toList();
}

// enrich the segments so that time conversions can be easily performed
// throughout the app
const processSegments = createMediaSegments;

// a dictionary to look up a list of media segments by an id
export const mediaSegmentsByIdSelector = createSelector(
  mediaSegmentDataByIdSelector,
  (rawDataById): MediaSegmentsById => {
    if (!rawDataById) {
      return EMPTY_OBJECT;
    }

    return rawDataById.map(data => processSegments(data.get('data'))).toJS();
  },
);

// selects an individual list from the mediaSegmentsById dictionary
export const mediaSegmentsSelector = createSelector(
  mediaSegmentsByIdSelector,
  (_, id: string) => id,
  (mediaSegmentsById, id) => {
    if (!mediaSegmentsById || !id) {
      return [];
    }

    return mediaSegmentsById[id];
  },
);
